import React, { useCallback, useMemo } from "react";
import { noop } from "@common/helpers/function";
import {
    COUNTERPARTY_GLOBAL_SOURCE_LABEL,
    COUNTERPARTY_SOURCE_LABELS,
} from "@common/helpers/counterparties";
import { CounterpartySource } from "@common/types/counterparty";
import { FinancialAccountSourceType } from "@common/types/financialAccount";
import { ConnectionProvider } from "@common/types/integrationConnection";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { useIntegrationConnections } from "@api/integrationConnection.api";
import { useFinancialAccounts } from "@hooks/useFinancialAccounts";
import {
    FilterMultiselect,
    FilterMultiselectOption,
    FilterMultiselectValue,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { BaseFilterProps } from "../../lib";
import styles from "../TransactionStatusFilter/TransactionStatusFilter.module.scss";

const KICK_VALUE = "kick";
// Mirrors the server-side bound on `createdByUserIds`
// (common/schemas/counterparty.schema.ts) to avoid a 400 with no UI feedback.
const MAX_CREATED_BY_USER_IDS = 100;

// Integration counterparty sources exposed as "Created by" filter options, in
// display order. Each is only shown when the workspace actually has that
// connection, so users can't filter by a source that returns nothing.
const INTEGRATION_SOURCE_FILTERS: CounterpartySource[] = [
    CounterpartySource.STRIPE,
    CounterpartySource.RAMP,
    CounterpartySource.MERCURY,
    CounterpartySource.QUICKBOOKS,
    CounterpartySource.BILLCOM,
];

const SOURCE_VALUES = new Set<string>(Object.values(CounterpartySource));

const isCounterpartySource = (
    value: FilterMultiselectValue,
): value is CounterpartySource =>
    typeof value === "string" && SOURCE_VALUES.has(value);

export const CounterpartyCreatedByFilter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => {
    const { data: members = [] } = useCurrentWorkspaceMembers();
    const financialAccounts = useFinancialAccounts();
    const { data: quickbooksConnections } = useIntegrationConnections({
        provider: ConnectionProvider.QUICKBOOKS,
    });

    const availableSources = useMemo((): Set<CounterpartySource> => {
        const accountSourceTypes = new Set(
            financialAccounts.map((account) => account.sourceType),
        );
        const sources = new Set<CounterpartySource>();
        if (accountSourceTypes.has(FinancialAccountSourceType.STRIPE)) {
            sources.add(CounterpartySource.STRIPE);
        }
        if (accountSourceTypes.has(FinancialAccountSourceType.RAMP)) {
            sources.add(CounterpartySource.RAMP);
        }
        if (accountSourceTypes.has(FinancialAccountSourceType.MERCURY)) {
            sources.add(CounterpartySource.MERCURY);
        }
        if (accountSourceTypes.has(FinancialAccountSourceType.BILLCOM)) {
            sources.add(CounterpartySource.BILLCOM);
        }
        if ((quickbooksConnections?.length ?? 0) > 0) {
            sources.add(CounterpartySource.QUICKBOOKS);
        }
        return sources;
    }, [financialAccounts, quickbooksConnections]);

    const selectedSources = useMemo(
        () => new Set(filters.createdBySources ?? []),
        [filters.createdBySources],
    );

    const options = useMemo((): FilterMultiselectOption[] => {
        const opts: FilterMultiselectOption[] = [
            {
                value: KICK_VALUE,
                label: COUNTERPARTY_GLOBAL_SOURCE_LABEL,
                searchableLabel: COUNTERPARTY_GLOBAL_SOURCE_LABEL,
            },
        ];

        for (const member of members) {
            const name = member.user.preferredName ?? member.user.name;
            opts.push({
                value: member.user.id,
                label: name,
                searchableLabel: `${name} ${member.user.email ?? ""}`,
            });
        }

        // Show a source when the workspace has that connection, and keep any
        // already-selected source visible even if the connection is still
        // loading or has since been removed, so the selection never renders
        // without a matching option.
        for (const source of INTEGRATION_SOURCE_FILTERS) {
            if (availableSources.has(source) || selectedSources.has(source)) {
                const label = COUNTERPARTY_SOURCE_LABELS[source];
                opts.push({
                    value: source,
                    label,
                    searchableLabel: label,
                });
            }
        }

        return opts;
    }, [members, availableSources, selectedSources]);

    const value = useMemo((): FilterMultiselectValue[] => {
        const selected: FilterMultiselectValue[] = [];
        if (filters.createdByKick) {
            selected.push(KICK_VALUE);
        }
        for (const id of filters.createdByUserIds ?? []) {
            selected.push(id);
        }
        for (const source of filters.createdBySources ?? []) {
            selected.push(source);
        }
        return selected;
    }, [
        filters.createdByKick,
        filters.createdByUserIds,
        filters.createdBySources,
    ]);

    const handleChange = useCallback(
        (val: FilterMultiselectValue[]) => {
            const createdByKick = val.includes(KICK_VALUE) || undefined;
            const createdByUserIds = val
                .filter((v): v is number => typeof v === "number")
                .slice(0, MAX_CREATED_BY_USER_IDS);
            const createdBySources = val.filter(isCounterpartySource);
            onChange({
                createdByKick,
                createdByUserIds:
                    createdByUserIds.length > 0 ? createdByUserIds : undefined,
                createdBySources:
                    createdBySources.length > 0 ? createdBySources : undefined,
            });
        },
        [onChange],
    );

    return (
        <div className={styles.filter}>
            <FilterMultiselect
                inDropdown={false}
                options={options}
                value={value}
                onChange={handleChange}
                name="createdByUserIds"
                onBlur={noop}
                searchable
            />
        </div>
    );
};
